import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock, DollarSign, Users, TrendingUp, Calendar, History, Check, X, Edit } from 'lucide-react';
import creditLimitService from '../services/creditLimitService';
import { showSuccess, showError } from '../services/toastService';
import './TenureManagement.css';

// UI Components
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import InfiniteScrollSentinel from './InfiniteScrollSentinel';

const TenureManagement = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
    const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
    const [selectedUser, setSelectedUser] = useState(null);
    const [creditModalOpen, setCreditModalOpen] = useState(false);
    const [transactionModalOpen, setTransactionModalOpen] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [creditForm, setCreditForm] = useState({
        is_credit_enabled: false,
        credit_limit: '',
        credit_tenure_days: 0
    });

    useEffect(() => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (searchTerm) next.set('search', searchTerm); else next.delete('search');
            return next;
        }, { replace: true });
    }, [searchTerm, setSearchParams]);

    useEffect(() => {
        fetchUsers();
        fetchStats();
    }, [pagination.page, searchTerm]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                search: searchTerm
            };
            const response = await creditLimitService.getAllUsersWithCredit(params);
            if (response.success) {
                setUsers((current) => pagination.page === 1 ? response.data.users : [...current, ...response.data.users]);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            showError(error.message || 'Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await creditLimitService.getCreditStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const handleEditCredit = (user) => {
        setSelectedUser(user);
        setCreditForm({
            is_credit_enabled: user.is_credit_enabled,
            credit_limit: user.credit_limit.toString(),
            credit_tenure_days: user.credit_tenure_days
        });
        setCreditModalOpen(true);
    };

    const handleCreditSubmit = async () => {
        if (!selectedUser) return;

        // Validation
        if (creditForm.is_credit_enabled) {
            const creditLimit = parseFloat(creditForm.credit_limit);
            if (isNaN(creditLimit) || creditLimit < 0) {
                showError('Please enter a valid credit limit');
                return;
            }
            if (!creditForm.credit_tenure_days || ![7, 15, 30].includes(creditForm.credit_tenure_days)) {
                showError('Please select a valid tenure (7, 15, or 30 days)');
                return;
            }
        }

        try {
            const response = await creditLimitService.setCreditLimitAndTenure(selectedUser.id, {
                is_credit_enabled: creditForm.is_credit_enabled,
                credit_limit: creditForm.is_credit_enabled ? parseFloat(creditForm.credit_limit) : 0,
                credit_tenure_days: creditForm.is_credit_enabled ? creditForm.credit_tenure_days : 0
            });

            if (response.success) {
                showSuccess('Credit settings updated successfully');
                setCreditModalOpen(false);
                fetchUsers();
                fetchStats();
            }
        } catch (error) {
            showError(error.message || 'Failed to update credit settings');
        }
    };

    const handleViewTransactions = async (user) => {
        setSelectedUser(user);
        try {
            const response = await creditLimitService.getUserCreditTransactions(user.id);
            if (response.success) {
                setTransactions(response.data.transactions);
                setTransactionModalOpen(true);
            }
        } catch (error) {
            showError(error.message || 'Failed to fetch transactions');
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getApprovalBadge = (status) => {
        const variants = {
            approved: 'active',
            pending: 'pending',
            rejected: 'destructive'
        };
        return (
            <Badge variant={variants[status] || 'inactive'}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
        );
    };

    const getTenureBadge = (days, expired = false) => {
        if (days === 0) return <Badge variant="inactive">No Tenure</Badge>;

        const variant = expired ? 'destructive' : 'active';
        return <Badge variant={variant}>{days} Days</Badge>;
    };

    const getCreditUsagePercentage = (used, limit) => {
        if (limit === 0) return 0;
        return Math.min(100, (used / limit) * 100);
    };

    return (
        <div className="tenure-management-container">
            {/* Header */}
            <div className="tm-header">
                <div>
                    <h1 className="tm-title">Credit Limit & Tenure Management</h1>
                    <p className="tm-subtitle">Manage user credit limits and payment tenure settings</p>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="tm-stats-grid">
                <Card>
                    <CardContent className="tm-stat-card">
                        <div className="tm-stat-icon">
                            <Users size={24} />
                        </div>
                        <div className="tm-stat-content">
                            <p className="tm-stat-label">Total Users</p>
                            <p className="tm-stat-value">{stats.total_users || 0}</p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="tm-stat-card">
                        <div className="tm-stat-icon tm-stat-icon-success">
                            <Clock size={24} />
                        </div>
                        <div className="tm-stat-content">
                            <p className="tm-stat-label">Credit Enabled</p>
                            <p className="tm-stat-value">{stats.credit_enabled_users || 0}</p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="tm-stat-card">
                        <div className="tm-stat-icon tm-stat-icon-warning">
                            <TrendingUp size={24} />
                        </div>
                        <div className="tm-stat-content">
                            <p className="tm-stat-label">Total Credit Limit</p>
                            <p className="tm-stat-value">{formatCurrency(stats.total_credit_limit || 0)}</p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="tm-stat-card">
                        <div className="tm-stat-icon tm-stat-icon-danger">
                            <DollarSign size={24} />
                        </div>
                        <div className="tm-stat-content">
                            <p className="tm-stat-label">Credit Used</p>
                            <p className="tm-stat-value">{formatCurrency(stats.total_credit_used || 0)}</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Users Table */}
            <Card>
                <CardHeader>
                    <CardTitle>Users Credit Management</CardTitle>
                </CardHeader>
                <CardContent>
                    {/* Filters */}
                    <div className="tm-filters">
                        <Input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder="Search by business name, email, or contact person..."
                            className="tm-search-input"
                        />
                    </div>

                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Business Name</TableHead>
                                <TableHead>Contact</TableHead>
                                <TableHead>Approval</TableHead>
                                <TableHead>Credit Status</TableHead>
                                <TableHead>Credit Limit</TableHead>
                                <TableHead>Usage</TableHead>
                                <TableHead>Tenure</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan="8" className="tm-table-empty">Loading...</TableCell>
                                </TableRow>
                            ) : users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan="8" className="tm-table-empty">No users found</TableCell>
                                </TableRow>
                            ) : (
                                users.map(user => (
                                    <TableRow key={user.id}>
                                        <TableCell>
                                            <div>
                                                <div className="tm-business-name">{user.business_name}</div>
                                                <div className="tm-email">{user.email}</div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div>
                                                <div className="tm-contact-person">{user.contact_person}</div>
                                                <div className="tm-phone">{user.phone}</div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {getApprovalBadge(user.approval_status)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="tm-credit-status">
                                                {user.is_credit_enabled ? (
                                                    <Badge variant="active">Enabled</Badge>
                                                ) : (
                                                    <Badge variant="inactive">Disabled</Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="tm-credit-limit">
                                                {user.is_credit_enabled ? (
                                                    <span className="tm-limit-amount">
                                                        {formatCurrency(user.credit_limit)}
                                                    </span>
                                                ) : (
                                                    <span className="tm-no-credit">No Credit</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {user.is_credit_enabled ? (
                                                <div className="tm-usage-container">
                                                    <div className="tm-usage-bar">
                                                        <div
                                                            className="tm-usage-fill"
                                                            style={{
                                                                width: `${getCreditUsagePercentage(user.credit_used, user.credit_limit)}%`
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="tm-usage-text">
                                                        {formatCurrency(user.credit_used)} / {formatCurrency(user.credit_limit)}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="tm-no-usage">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="tm-tenure-info">
                                                {getTenureBadge(user.credit_tenure_days, user.tenure_expired)}
                                                {user.is_credit_enabled && user.days_remaining !== undefined && (
                                                    <div className="tm-days-remaining">
                                                        {user.days_remaining} days left
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="tm-actions">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleEditCredit(user)}
                                                    disabled={user.approval_status !== 'approved'}
                                                    className="tm-edit-btn"
                                                    title="Edit credit settings"
                                                >
                                                    <Edit size={16} />
                                                </Button>
                                                {user.is_credit_enabled && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleViewTransactions(user)}
                                                        className="tm-history-btn"
                                                        title="View transaction history"
                                                    >
                                                        <History size={16} />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <InfiniteScrollSentinel
                        hasMore={pagination.page < pagination.pages}
                        loading={loading}
                        onLoadMore={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    />
                </CardContent>
            </Card>

            {/* Credit Settings Modal */}
            <Dialog open={creditModalOpen} onOpenChange={setCreditModalOpen}>
                <DialogContent className="tm-modal">
                    <DialogHeader>
                        <DialogTitle>Credit Settings</DialogTitle>
                        <DialogDescription>
                            Configure credit limit and tenure for <strong>{selectedUser?.business_name}</strong>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="tm-modal-content">
                        {/* Enable Credit Toggle */}
                        <div className="tm-form-section">
                            <div className="tm-form-group">
                                <label className="tm-form-label">Credit Payment</label>
                                <div className="tm-toggle-container">
                                    <button
                                        onClick={() => setCreditForm(prev => ({ ...prev, is_credit_enabled: !prev.is_credit_enabled }))}
                                        className={`tm-toggle ${creditForm.is_credit_enabled ? 'tm-toggle-on' : 'tm-toggle-off'}`}
                                    >
                                        <div className="tm-toggle-slider">
                                            {creditForm.is_credit_enabled ? <Check size={12} /> : <X size={12} />}
                                        </div>
                                    </button>
                                    <span className="tm-toggle-label">
                                        {creditForm.is_credit_enabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Credit Settings (only if enabled) */}
                        {creditForm.is_credit_enabled && (
                            <>
                                <div className="tm-form-section">
                                    <div className="tm-form-group">
                                        <label className="tm-form-label">Credit Limit (₹)</label>
                                        <Input
                                            type="number"
                                            value={creditForm.credit_limit}
                                            onChange={(e) => setCreditForm(prev => ({ ...prev, credit_limit: e.target.value }))}
                                            placeholder="50000"
                                            min="0"
                                            step="1000"
                                            className="tm-form-input"
                                        />
                                    </div>
                                </div>

                                <div className="tm-form-section">
                                    <div className="tm-form-group">
                                        <label className="tm-form-label">Credit Tenure</label>
                                        <select
                                            value={creditForm.credit_tenure_days}
                                            onChange={(e) => setCreditForm(prev => ({ ...prev, credit_tenure_days: parseInt(e.target.value) }))}
                                            className="tm-form-select"
                                        >
                                            <option value={0}>Select Tenure</option>
                                            <option value={7}>7 Days</option>
                                            <option value={15}>15 Days</option>
                                            <option value={30}>30 Days</option>
                                        </select>
                                        <small className="tm-form-hint">
                                            Credit tenure determines how long the user can use credit before payment is due
                                        </small>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreditModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreditSubmit}>
                            Save Settings
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Transaction History Modal */}
            <Dialog open={transactionModalOpen} onOpenChange={setTransactionModalOpen}>
                <DialogContent className="tm-transaction-modal">
                    <DialogHeader>
                        <DialogTitle>Credit Transaction History</DialogTitle>
                        <DialogDescription>
                            Transaction history for <strong>{selectedUser?.business_name}</strong>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="tm-transaction-content">
                        {transactions.length === 0 ? (
                            <div className="tm-no-transactions">
                                <Calendar size={48} />
                                <p>No transactions found</p>
                            </div>
                        ) : (
                            <div className="tm-transaction-list">
                                {transactions.map((transaction, index) => (
                                    <div key={transaction.id} className="tm-transaction-item">
                                        <div className="tm-transaction-info">
                                            <div className="tm-transaction-type">
                                                {transaction.transaction_type.replace('_', ' ').toUpperCase()}
                                            </div>
                                            <div className="tm-transaction-description">
                                                {transaction.description}
                                            </div>
                                            <div className="tm-transaction-date">
                                                {formatDate(transaction.created_at || transaction.createdAt)}
                                            </div>
                                        </div>
                                        <div className="tm-transaction-amount">
                                            <div className={`tm-amount ${transaction.transaction_type === 'credit_used' ? 'tm-amount-negative' : 'tm-amount-positive'}`}>
                                                {transaction.transaction_type === 'credit_used' ? '-' : '+'}
                                                {formatCurrency(transaction.amount)}
                                            </div>
                                            <div className="tm-balance">
                                                Balance: {formatCurrency(transaction.balance_after)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTransactionModalOpen(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div >
    );
};

export default TenureManagement;